/**
 * Authenticated dashboard API client — same-origin relative /v1 with credentials.
 * Never persists auth secrets in browser storage.
 */

import { getMemoryCsrfToken } from "./auth";

function apiBase(): string {
  // Prefer empty same-origin in production and Vite-proxied dev.
  if (import.meta.env.PROD) return "";
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv !== undefined && fromEnv.trim() === "") return "";
  if (import.meta.env.DEV) return "";
  return "";
}

export type DashboardLoadState<T> =
  | { status: "loading" }
  | { status: "ok"; data: T }
  | { status: "empty"; data: T }
  | { status: "denied"; message: string; code?: string }
  | { status: "error"; message: string; code?: string };

type ApiErrorBody = {
  error?: { code?: string; message?: string };
};

async function dashboardFetch<T>(path: string): Promise<DashboardLoadState<T>> {
  try {
    const headers = new Headers({ Accept: "application/json" });
    const csrf = getMemoryCsrfToken();
    if (csrf) headers.set("X-CSRF-Token", csrf);

    const res = await fetch(`${apiBase()}${path}`, {
      method: "GET",
      headers,
      credentials: "include",
      cache: "no-store",
    });

    if (res.status === 401 || res.status === 403) {
      let code: string | undefined;
      let message = res.status === 401 ? "Authentication required" : "Access denied";
      try {
        const body = (await res.json()) as ApiErrorBody;
        code = body.error?.code;
        if (body.error?.message) message = body.error.message;
      } catch {
        /* ignore */
      }
      return { status: "denied", message, code };
    }

    if (!res.ok) {
      let code: string | undefined;
      let message = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as ApiErrorBody;
        code = body.error?.code;
        if (body.error?.message) message = body.error.message;
      } catch {
        /* ignore */
      }
      return { status: "error", message, code };
    }

    const data = (await res.json()) as T & { empty?: boolean };
    if (data && typeof data === "object" && "empty" in data && data.empty === true) {
      return { status: "empty", data };
    }
    return { status: "ok", data };
  } catch (err) {
    return {
      status: "error",
      message: err instanceof Error ? err.message : "Network error",
    };
  }
}

export const fetchDashboardOverview = () => dashboardFetch<DashboardOverview>("/v1/dashboard/overview");
export const fetchDashboardTenant = () => dashboardFetch<DashboardTenant>("/v1/dashboard/tenant");
export const fetchDashboardCopyNetwork = () =>
  dashboardFetch<DashboardCopyNetwork>("/v1/dashboard/copy-network");
export const fetchDashboardLeaders = () => dashboardFetch<DashboardLeaders>("/v1/dashboard/leaders");
export const fetchDashboardFollowers = () =>
  dashboardFetch<DashboardFollowers>("/v1/dashboard/followers");
export const fetchDashboardRisk = () => dashboardFetch<DashboardRisk>("/v1/dashboard/risk");
export const fetchDashboardBridge = () => dashboardFetch<DashboardBridge>("/v1/dashboard/bridge");
export const fetchDashboardAudit = () => dashboardFetch<DashboardAudit>("/v1/dashboard/audit?limit=25");
export const fetchDashboardSupport = () => dashboardFetch<DashboardSupport>("/v1/dashboard/support");
export const fetchDashboardSettings = () =>
  dashboardFetch<DashboardSettings>("/v1/dashboard/settings");

export type DashboardEnvelope = {
  tenantId: string;
  readOnly: true;
  dryRun: true;
  executionAffected: false;
  liveExecutionEnabled: boolean;
  executionMode: string;
  bridgeMode: string;
};

export type DashboardMonitoringPosture = {
  sloStatus: "healthy" | "at_risk" | "exhausted" | "insufficient_data";
  sampleCount: number;
  firingAlertCount: number;
  firingBySeverity: { P0: number; P1: number; P2: number; P3: number };
  liveExecutionEnabled: boolean;
  brokerNetworkEnabled: boolean;
  tlsDaysRemaining: number | null;
  tlsNote: string;
  lastMonitorAt: string | null;
  rollbackReady: boolean;
  processUptimeSeconds: number;
};

export type DashboardOverview = DashboardEnvelope & {
  counts: {
    leaders: number;
    followers: number;
    relationships: number;
    activeRelationships: number;
    brokers: number;
    openRiskViolations: number;
    activeRiskPolicies: number;
    activeApiKeys: number;
  };
  safety: {
    liveExecutionEnabled: boolean;
    executionMode: string;
    bridgeMode: string;
    killSwitchActive: boolean;
    rolloutMode: string;
  };
  storageMode: string;
  uptimeSeconds: number;
  monitoring?: DashboardMonitoringPosture;
};

export type DashboardTenant = DashboardEnvelope & {
  tenant: {
    slug: string;
    name: string;
    executionMode: string;
    bridgeMode: string;
    liveExecutionEnabled: boolean;
    killSwitchActive: boolean;
    activeApiKeyCount: number;
    createdAt: string;
    updatedAt: string;
  };
};

export type DashboardCopyNetwork = DashboardEnvelope & {
  edges: Array<{
    relationshipId: string;
    leaderLabel: string;
    followerLabel: string;
    status: string;
  }>;
  empty: boolean;
};

export type DashboardLeaders = DashboardEnvelope & {
  leaders: Array<{
    leaderId: string;
    name: string;
    leaderAccountId: string;
    brokerProvider: string;
    followerCount: number;
    createdAt: string;
    updatedAt: string;
  }>;
  empty: boolean;
};

export type DashboardFollowers = DashboardEnvelope & {
  followers: Array<{
    followerId: string;
    name: string | null;
    followerAccountId: string;
    brokerProvider: string | null;
    relationshipCount: number;
    createdAt: string;
    updatedAt: string;
  }>;
  empty: boolean;
};

export type DashboardRisk = DashboardEnvelope & {
  controls: Array<{ name: string; value: string; tone: "ok" | "warn" | "neutral" | "locked" }>;
  policies: Array<{ policyId: string; name: string; status: string; updatedAt: string }>;
  openViolations: number;
  emptyPolicies: boolean;
};

export type DashboardBridge = DashboardEnvelope & {
  connections: Array<{
    connectionId: string;
    brokerId: string;
    provider: string;
    mode: string;
    status: string;
    healthStatus: string;
  }>;
  brokerCount: number;
  queueStatus: string;
  empty: boolean;
};

export type DashboardAudit = DashboardEnvelope & {
  entries: Array<{
    at: string;
    action: string;
    entityType: string | null;
    entityId: string | null;
    actorType: string | null;
    actorId: string | null;
    reason: string | null;
  }>;
  count: number;
  limit: number;
  empty: boolean;
};

export type DashboardSupport = DashboardEnvelope & {
  posture: {
    openRiskViolations: number;
    activeRelationships: number;
    brokerConnections: number;
    recentAuditCount: number;
  };
  monitoring?: DashboardMonitoringPosture;
  lanes: { P0: []; P1: []; P2: []; P3: [] };
  note: string;
};

export type DashboardSettings = DashboardEnvelope & {
  productName: string;
  safety: {
    liveExecutionEnabled: boolean;
    executionMode: string;
    bridgeMode: string;
    killSwitchActive: boolean;
    rolloutMode: string;
  };
  storageMode: string;
  viewer: { homeTenantId: string; principalType: string } | null;
  apiBaseMode: string;
  domainStatus?: {
    customDomainConfigured: boolean;
    customDomainProven: boolean;
    canonicalWebOrigin: string | null;
    canonicalApiOrigin: string | null;
    railwayFallbackWebOrigin: string | null;
    canonicalRedirectEnabled: boolean;
    hstsEnabled: boolean;
    futureWebAuthnRpId: string | null;
    futureWebAuthnAllowedOrigins: string[];
  };
};
